import { useEffect, useState } from 'react';
import BaseAgreementBottomSheet from './components/BaseAgreementBottomSheet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { getJWT } from '@pocapan/shared/utils/etcUtils';
import saveData from '@pocapan/shared/utils/saveData';
import { getAppSettings } from '@pocapan/shared/utils/NativeUtil';
import {
  getClientSocialPlatform,
  getSocialLoginMethods,
  resolveEnabledSocialLoginMethods,
  type SocialLoginMethod,
  type SocialLoginMethodsResponse
} from '../../data/repositories/SocialLoginMethods';
import { normalizeLocale } from '../../i18n/loadNamespace';

const MIN_DELAY = 1000;
const SOCIAL_METHODS_PREFETCH_TIMEOUT_MS = 3000;

const normalizeLanguage = (lang?: string | null): string | null => {
  if (!lang || typeof lang !== 'string') return null;
  return normalizeLocale(lang);
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [prefetchedSocialMethods, setPrefetchedSocialMethods] = useState<SocialLoginMethod[]>([]);
  const [isSocialMethodsPrefetching, setIsSocialMethodsPrefetching] = useState(true);
  const [socialMethodsPrefetchError, setSocialMethodsPrefetchError] = useState<string | null>(null);
  const [isPrefetchDone, setIsPrefetchDone] = useState(false);
  const [isMinDelayPassed, setIsMinDelayPassed] = useState(false);

  const token = getJWT();

  useEffect(() => {
    if (token && navigate) {
      navigate('/home', { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    const applyAppSettings = async () => {
      const settings = (await getAppSettings()) as {
        language?: string;
        country?: string;
      } | null;

      if (!settings) return;

      const locale = normalizeLanguage(settings.language);
      if (locale && locale !== i18n.language) {
        saveData('i18nextLng', locale);
        i18n.changeLanguage(locale);
      }

      if (settings.country) {
        saveData('feedCountryCode', settings.country);
      }
    };
    applyAppSettings();
  }, [i18n]);

  // 1초 최소 딜레이
  useEffect(() => {
    const timer = setTimeout(() => setIsMinDelayPassed(true), MIN_DELAY);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setIsSocialMethodsPrefetching(true);
    setSocialMethodsPrefetchError(null);

    const platform = getClientSocialPlatform();
    let timeoutId: ReturnType<typeof window.setTimeout> | undefined;
    const socialMethodsTimeout = new Promise<SocialLoginMethodsResponse>((resolve) => {
      timeoutId = window.setTimeout(() => {
        resolve({
          success: false,
          platform,
          loginMethods: [],
          enabledLoginMethods: [],
          errorMessage: t('requestVerificationCodePage.socialLoginMethodsFailedMessage')
        });
      }, SOCIAL_METHODS_PREFETCH_TIMEOUT_MS);
    });

    Promise.race([getSocialLoginMethods({ platform }), socialMethodsTimeout])
      .then((response) => {
        if (!isMounted) return;

        const resolvedMethods = resolveEnabledSocialLoginMethods(response, platform);
        setPrefetchedSocialMethods(resolvedMethods);

        if (response.success === false && response.errorMessage) {
          setSocialMethodsPrefetchError(response.errorMessage);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setPrefetchedSocialMethods([]);
        setSocialMethodsPrefetchError(
          t('requestVerificationCodePage.socialLoginMethodsFailedMessage')
        );
      })
      .finally(() => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
        if (!isMounted) return;
        setIsSocialMethodsPrefetching(false);
        setIsPrefetchDone(true);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const startLogin = (isPromotionAgreed: boolean) => {
    navigate(
      `/request-verification-code?isPromotionAgreed=${isPromotionAgreed}&isTermsAgreed=true`,
      { replace: true }
    );
  };

  const handleAgreement = (isChild: boolean, isPromotionAgreed: boolean) => {
    if (!isChild) {
      startLogin(isPromotionAgreed);
    }
  };

  if (token) {
    return <div />;
  }

  const isSheetOpen = isPrefetchDone && isMinDelayPassed;

  return (
    <div className="relative overflow-hidden mx-auto max-w-xl h-[calc(var(--vh,1vh)*100)] pt-[env(safe-area-inset-top)]">
      {/* 홀로그래피 비디오 배경 */}
      <video
        className="absolute inset-0 w-full h-full object-cover z-0"
        src="/assets/backgrounds/bg_holography.mp4"
        autoPlay
        muted
        playsInline
        loop
        tabIndex={-1}
      />

      {/* 딤 오버레이 */}
      <div className="absolute inset-0 z-[1]" />

      {/* 로고 */}
      <div className="absolute top-[calc(env(safe-area-inset-top)+125px)] left-0 right-0 flex justify-center z-10">
        <img src="/assets/logos/pocapan.png" alt="pocapan" className="w-[310px] object-contain" />
      </div>

      {isSheetOpen && (
        <BaseAgreementBottomSheet
          isOpen={true}
          onAgreementClick={(isChild: boolean, isPromotionAgreed: boolean) =>
            handleAgreement(isChild, isPromotionAgreed)
          }
          title={false}
          description={false}
          transparentBackground={true}
          showLoginMethods={true}
          prefetchedSocialMethods={prefetchedSocialMethods}
          isSocialMethodsPrefetching={isSocialMethodsPrefetching}
          socialMethodsPrefetchError={socialMethodsPrefetchError || undefined}
          sheetMaxWidth="36rem"
        />
      )}
    </div>
  );
}
